import { createCallbackTriggers } from "../utils/callbacks/callbackTriggers";

/**
 * SessionCoordinator - Handles session management and callback coordination
 */
export class SessionCoordinator {
  constructor(sessionManager, store) {
    this.sessionManager = sessionManager;
    this.store = store;
  }

  /**
   * Initialize a new command session
   */
  initializeSession(commandName, command, initialData, promptResolver) {
    console.log("🚀 Starting session for multi-step command...");
    this.sessionManager.startSession(commandName, command, initialData);

    const sessionData = this.sessionManager.getSessionData();
    const callbacks = this.createCallbackTriggers(commandName, sessionData);

    return { sessionData, callbacks };
  }

  /**
   * Set engine reference for callbacks
   */
  setEngineReference(engine) {
    this.engine = engine;
  }

  /**
   * Create callback triggers for commands to use
   */
  createCallbackTriggers(commandName, sessionData = {}) {
    console.log("🔍 Creating callback triggers:", {
      hasStore: !!this.store,
      hasRegistry: !!this.store?.callbackRegistry,
      registryType: typeof this.store?.callbackRegistry,
      registryKeys: this.store?.callbackRegistry?.getAll
        ? Object.keys(this.store.callbackRegistry.getAll())
        : "no getAll method",
    });

    if (!this.store || !this.store.callbackRegistry) {
      console.warn("⚠️ No callback registry available in store");
      return {};
    }

    const context = {
      commandName,
      sessionData,
      timestamp: new Date().toISOString(),
      engine: this.engine, // Use the engine reference
    };

    const triggers = createCallbackTriggers(
      this.store.callbackRegistry,
      context
    );
    console.log("✅ Created callback triggers:", Object.keys(triggers));
    return triggers;
  }

  /**
   * Enhanced session ending that also clears store state
   */
  endSession() {
    this.sessionManager.endSession();

    // Clear store command state if store is available
    if (this.store && this.store.clearCommandState) {
      this.store.clearCommandState();
    }
  }

  /**
   * Get current session information
   */
  getSessionInfo() {
    return {
      isInSession: this.sessionManager.isInSession(),
      currentStep: this.sessionManager.getCurrentStep(),
      sessionData: this.sessionManager.getSessionData(),
      currentStepIndex: this.sessionManager.getCurrentStepIndex(),
      totalSteps: this.sessionManager.getTotalSteps(),
      hasMoreSteps: this.sessionManager.hasMoreSteps(),
    };
  }

  /**
   * Move to next step
   */
  moveToNextStep() {
    this.sessionManager.moveToNextStep();
  }

  /**
   * Check if command is stepless
   */
  isSteplessCommand(command) {
    return (
      (!command.steps || command.steps.length === 0) &&
      (command.onComplete || command.intro || command.outro)
    );
  }
}
